import useSWR from 'swr'
import { create } from 'zustand'
import React from 'react'
import DropdownState from 'components/DropdownState'
import Loader from '@/components/ui/Loader'
import MyPagination from 'components/ui/MyPagination'
import DisplayData from './DisplayData'
import CreatePage from './Create'
import MyRefetchButton from 'components/ui/MyRefetchButton'
import Dropdown from 'components/ui/Selector'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { ContentLangs } from 'config'
import { X } from 'lucide-react'

const LANG_OPTIONS = ContentLangs.filter((item) => item.code > 0)
const PAGE_SIZE = 90

const ORDER_OPTIONS = [
  { code: 'updateTime', name: 'updateTime' },
  { code: 'likeCount', name: 'likeCount' },
  {
    code: 'dailyCount.reCreateCount',
    name: 'dailyCount.reCreateCount',
  },
  { code: 'reCreateCount', name: 'reCreateCount' },
  {
    code: 'dailyCount.downloadCount',
    name: 'dailyCount.downloadCount',
  },
  { code: 'dCount', name: 'dCount' },
  { code: 'reportCount', name: 'reportCount' },
]

const DEFAULT_FILTERS = {
  lang: null,
  order: 'updateTime',
  id: '',
  userId: '',
  state: '401',
  after: null,
  afters: [],
}

export const useForm = create((set) => ({
  ...DEFAULT_FILTERS,
  updateLang: (value) => set({ lang: value, after: null, afters: [] }),
  updateOrder: (value) => set({ order: value, after: null, afters: [] }),
  updateId: (value) => set({ id: value, after: null, afters: [] }),
  updateUserId: (value) => set({ userId: value, after: null, afters: [] }),
  updateState: (value) => set({ state: value, after: null, afters: [] }),
  filterByUserId: (userId) =>
    set({
      ...DEFAULT_FILTERS,
      userId: userId || '',
    }),
  goAfter: (nextAfter) =>
    set((state) => {
      if (nextAfter === null) {
        return { after: null }
      }
      const index = state.afters.findIndex((a) => a === nextAfter)
      if (index === -1) {
        return { after: nextAfter, afters: [...state.afters, nextAfter] }
      } else {
        return { after: nextAfter }
      }
    }),
}))

const Index = () => {
  const params = useForm((s) => {
    const { lang, order, after, id, userId, state } = s
    return {
      lang: lang > 0 ? lang : null,
      order,
      after,
      id,
      authorId: userId,
      state,
      limit: PAGE_SIZE,
    }
  })

  const {
    afters,
    after,
    goAfter,
    lang,
    updateLang,
    order,
    updateOrder,
    id,
    updateId,
    userId,
    updateUserId,
    state,
    updateState,
    filterByUserId,
  } = useForm((s) => s)

  const handleAuthorClick = (authorId) => {
    filterByUserId(authorId)
  }

  const { data, isLoading, isValidating, mutate } = useSWR(['/ocs', params])
  const nextAfter =
    data?.length >= PAGE_SIZE ? data[data.length - 1]?.id : undefined
  return (
    <div className="flex h-[calc(100vh-98px)] flex-col space-y-4">
      <div className="flex-shrink-0">
        <h2 className="text-3xl font-bold tracking-tight">Ocs</h2>
      </div>
      {(isLoading || isValidating) && <Loader />}
      {!isLoading && !isValidating && (
        <>
          <div className="flex w-full flex-shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
              <div className="flex shrink-0 items-center gap-4">
                <MyPagination
                  nextAfter={nextAfter}
                  after={after}
                  goAfter={goAfter}
                  afters={afters}
                  className="m-0 w-auto"
                />
                <MyRefetchButton onClick={mutate} />
              </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="id-filter"
                className="shrink-0 text-sm font-medium"
              >
                ID:
              </label>
              <div className="relative">
                <Input
                  id="id-filter"
                  type="text"
                  placeholder="OC ID"
                  value={id}
                  onChange={(e) => updateId(e.target.value)}
                  className="w-96 max-w-full"
                />
                {id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => updateId('')}
                    className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="userid-filter"
                className="shrink-0 text-sm font-medium"
              >
                UserID:
              </label>
              <div className="relative">
                <Input
                  id="userid-filter"
                  type="text"
                  placeholder="User ID"
                  value={userId}
                  onChange={(e) => updateUserId(e.target.value)}
                  className="w-80 max-w-full"
                />
                {userId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => updateUserId('')}
                    className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="lang-filter"
                className="shrink-0 text-sm font-medium"
              >
                Lang:
              </label>
              <div className="relative">
                <Dropdown
                  datas={LANG_OPTIONS}
                  showLabel
                  showCode
                  labelName="Lang"
                  value={lang}
                  update={updateLang}
                  placeholder="All"
                />
                {lang > 0 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    title="Clear lang"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      updateLang(null)
                    }}
                    className="absolute right-8 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="order-filter"
                className="shrink-0 text-sm font-medium"
              >
                Order:
              </label>
              <Dropdown
                datas={ORDER_OPTIONS}
                showLabel
                labelName="Order"
                value={order}
                update={updateOrder}
              />
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="state-filter"
                className="shrink-0 text-sm font-medium"
              >
                State:
              </label>
              <DropdownState value={state} update={updateState} />
            </div>
            </div>
            <div className="shrink-0">
              <CreatePage mutate={mutate} />
            </div>
          </div>
          {data?.length <= 0 && <div>no data</div>}
          {data?.length > 0 && (
            <div className="min-h-0 flex-1">
              <DisplayData
                data={data}
                mutate={mutate}
                fillContainer
                onAuthorClick={handleAuthorClick}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default Index
